import { pdfContainerSx } from './PdfSxs';
import { PdfThirdCard } from './PdfThirdCard';
import { PdfSecondCard } from './PdfSecondCard';
import { PdfFirstCard } from './PdfFirstCard';
import { PdfGraphDetailBox } from './PdfGraphDetailBox';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { PdfGscCard } from './PdfGscCard';
import { PdfKeywordTracking } from './PdfKeywordTracking';
import { PdfAiCards } from './PdfAiCards';
import { selectSectionVisibilityMap } from 'src/content/pdfBuilder/redux/selectors';
import { PDFSection } from 'src/content/pdfBuilder/types';
import { Box } from '@mui/material';

interface PdfCardsProps {
  isPdfBuilder?: boolean;
  isExporting?: boolean;
  reportMetaData?: any;
}

export const PdfCards: React.FC<PdfCardsProps> = ({
  isPdfBuilder,
  isExporting,
  reportMetaData
}) => {
  const pdfReportData = useSelector(
    (state: RootState) => state.dashboard.pdfReportData
  );

  const siteDetails: any =
    useSelector((state: RootState) => state.siteAudit.siteAuditDetails) || null;
  const activePlanName = useSelector(
    (state: RootState) => state.user.activePlan
  );
  const planDetails = useSelector((state: RootState) => state.user.planDetails);
  const isUnlocked =
    useSelector((state: RootState) => state.keywordResearch.isUnlocked) ??
    false;

  // Derive connection states from pdfReportData or siteDetails fallback
  const isGsc =
    pdfReportData?.config?.is_gsc ??
    siteDetails?.is_gsc ??
    pdfReportData?.isGsc ??
    false;
  const isRevoke = pdfReportData?.graphDetails?.totalMetrics?.isRevoke ?? false;
  const isNoGsc = isGsc === false || isRevoke === true;

  // Derive AI Visibility status
  const aiVisibilityData =
    pdfReportData?.aiVisibilityDetails?.topics?.length > 0;
  const isShowAiCards = !!aiVisibilityData;

  // Derive Keyword Tracking status
  const isAgency =
    activePlanName === planDetails?.agency ||
    activePlanName === planDetails?.expertPlus ||
    activePlanName === planDetails?.enterprise;

  const hasPositionSummary =
    !!pdfReportData?.keywordTracking?.avgCalculationDetails?.positionSummary &&
    !!pdfReportData?.keywordTracking?.avgCalculationDetails?.lastUpdated;

  const isKeywordTracking =
    Boolean(isAgency || isUnlocked || isPdfBuilder || isExporting) &&
    hasPositionSummary;

  // Get section visibility from Redux
  const sectionVisibility = useSelector(selectSectionVisibilityMap);

  return (
    <>
      {/* Site Overview Section */}
      <Box
        className="keep-together"
        sx={{
          display: sectionVisibility[PDFSection.OVERVIEW] ? 'block' : 'none'
        }}
      >
        <div
          className="mt-6 mb-6"
          style={{
            ...pdfContainerSx?.pdfMainBoxSx,
            ...pdfContainerSx?.commonBoxMarginSx
          }}
        >
          <PdfFirstCard isNoGsc={isNoGsc} reportMetaData={reportMetaData} />
          {isNoGsc ? (
            <PdfGscCard />
          ) : (
            <>
              <PdfSecondCard
                isPdfBuilder={isPdfBuilder}
                reportMetaData={reportMetaData}
              />
              <PdfThirdCard
                isPdfBuilder={isPdfBuilder}
                reportMetaData={reportMetaData}
              />
            </>
          )}
        </div>
      </Box>

      {/* AI Visibility Section */}
      <Box
        className="keep-together"
        sx={{
          display:
            sectionVisibility[PDFSection.AI_VISIBILITY] && isShowAiCards
              ? 'block'
              : 'none'
        }}
      >
        <div className="mt-6 mb-6">
          <PdfAiCards />
        </div>
      </Box>

      {/* Graph Detail Section */}
      <Box
        className="keep-together"
        sx={{
          display: sectionVisibility[PDFSection.OVERVIEW] ? 'block' : 'none'
        }}
      >
        <div
          className="mt-6 mb-6"
          style={{ ...pdfContainerSx?.commonBoxMarginSx }}
        >
          <PdfGraphDetailBox
            isPdfBuilder={isPdfBuilder}
            reportMetaData={reportMetaData}
          />
        </div>
      </Box>

      {/* Keyword Tracking Section */}
      <Box
        className="keep-together"
        sx={{
          display:
            sectionVisibility[PDFSection.KEYWORD_TRACKING] && isKeywordTracking
              ? 'block'
              : 'none'
        }}
      >
        <div
          className="mt-6 mb-6"
          style={{ ...pdfContainerSx?.commonBoxMarginSx }}
        >
          <PdfKeywordTracking
            isPdfBuilder={isPdfBuilder}
            reportMetaData={reportMetaData}
          />
        </div>
      </Box>
    </>
  );
};
